/**
 * Settings → Channels (IR-68).
 *
 * WhatsApp and Meta (Instagram through Facebook Pages) are real: they call the
 * backend's /integrations/whatsapp and /integrations/meta-inbox endpoints.
 * Telegram is still MOCKED against an in-memory list persisted to localStorage
 * (seeded like demo-ui's seedChannelConnections) until its backend exists.
 *
 * TODO(IR-68 backend, Telegram): replace the mock with axios calls —
 *   POST   /channels/telegram/connect         { token }
 *   DELETE /channels/{id}
 */
import axios from "@utils/axios-instance";
import { getErrorFromBackend } from "@/utils/errors";
import {
  type TChannelConnection,
  type TConnectableProvider,
  type TConnectTelegramPayload,
  type TConnectWhatsAppPayload,
  type TMetaInboxConfig,
  type TMetaPage,
  type TWhatsAppConfig,
  type TWhatsAppNumber,
} from "@/models/channels.model";

const STORAGE_KEY = "ironpipeline.channels.mock";
const LATENCY_MS = 700;

/** Bot token format issued by @BotFather: `<bot id>:<secret>`. */
const TELEGRAM_TOKEN_RE = /^\d{6,}:[\w-]{30,}$/;

function seedTelegram(): TChannelConnection {
  return {
    id: "conn-telegram",
    provider: "telegram",
    status: "error",
    accounts: [{ channel: "telegram", label: "Bot", value: "@NexgenTSalesBot" }],
    enabledChannels: ["telegram"],
    error: "Bot token was revoked — reconnect to keep receiving messages.",
  };
}

function load(): TChannelConnection {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    // Older builds stored every provider here; only Telegram is still mocked.
    const stored = raw ? (JSON.parse(raw) as TChannelConnection[]) : [];
    const telegram = stored.find((c) => c.provider === "telegram");
    if (telegram) return telegram;
  } catch {
    // Corrupt or blocked storage — fall back to the seed.
  }
  return seedTelegram();
}

let telegramConnection: TChannelConnection = load();

function saveTelegram(next: TChannelConnection): TChannelConnection {
  telegramConnection = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([telegramConnection]));
  } catch {
    // Storage unavailable (private window) — keep the in-memory state only.
  }
  return telegramConnection;
}

const delay = (ms = LATENCY_MS) => new Promise((resolve) => setTimeout(resolve, ms));

/** Mimics an axios error so getErrorFromBackend() renders the message. */
const apiError = (message: string) => ({ response: { data: { error: { message } } } });

/* ---------------------------------- WhatsApp (real API) ---------------------------------- */

const WHATSAPP_CONNECTION_ID = "conn-whatsapp";

/** A number that stopped working (token expired, number restricted…) and needs a reconnect. */
export const isWhatsAppNumberBroken = (n: TWhatsAppNumber) => n.status === "error" || n.needsReauth;

/**
 * The WhatsApp card is one provider connection holding the company's numbers:
 * disconnected with none, "action needed" when any number is broken, otherwise connected.
 */
function toWhatsAppConnection(numbers: TWhatsAppNumber[]): TChannelConnection {
  const broken = numbers.find(isWhatsAppNumberBroken);
  return {
    id: WHATSAPP_CONNECTION_ID,
    provider: "whatsapp",
    status: numbers.length === 0 ? "disconnected" : broken ? "error" : "connected",
    accounts: [],
    enabledChannels: numbers.length === 0 ? [] : ["whatsapp"],
    error: broken
      ? `${numberName(broken)}: ${broken.lastError ?? "reconnect this number to keep receiving messages."}`
      : undefined,
    connectedAt: numbers[0]?.connectedAt,
    numbers,
  };
}

/** How a number is named in messages: its label, else the number itself. */
export const numberName = (n: TWhatsAppNumber) => n.label || n.displayPhoneNumber || n.phoneNumberId || "WhatsApp number";

async function getWhatsAppConnection(): Promise<TChannelConnection> {
  try {
    const { data } = await axios.get<TWhatsAppNumber[]>("/integrations/whatsapp/numbers");
    return toWhatsAppConnection(data);
  } catch (e) {
    // One provider failing to load must not hide the others on the page.
    return {
      id: WHATSAPP_CONNECTION_ID,
      provider: "whatsapp",
      status: "error",
      accounts: [],
      enabledChannels: [],
      error: getErrorFromBackend(e),
      loadFailed: true,
    };
  }
}

export async function getWhatsAppConfig(): Promise<TWhatsAppConfig> {
  const { data } = await axios.get<TWhatsAppConfig>("/integrations/whatsapp/config");
  return data;
}

/** Every WhatsApp change returns the refreshed card, so the list always matches the backend. */
async function refreshedWhatsApp(): Promise<TChannelConnection> {
  const { data } = await axios.get<TWhatsAppNumber[]>("/integrations/whatsapp/numbers");
  return toWhatsAppConnection(data);
}

/** Adds a number (or reconnects the same one in place). */
export async function connectWhatsApp(payload: TConnectWhatsAppPayload): Promise<TChannelConnection> {
  await axios.post<TWhatsAppNumber>("/integrations/whatsapp/connect", payload);
  return refreshedWhatsApp();
}

/** Development only: connects Meta's test number without Embedded Signup (404 on production). */
export async function connectWhatsAppTestNumber(): Promise<TChannelConnection> {
  await axios.post<TWhatsAppNumber>("/integrations/whatsapp/connect-test");
  return refreshedWhatsApp();
}

export async function renameWhatsAppNumber(id: number, label: string): Promise<TChannelConnection> {
  await axios.patch<TWhatsAppNumber>(`/integrations/whatsapp/numbers/${id}`, { label });
  return refreshedWhatsApp();
}

/** Disconnects one number; the company's other numbers keep working. */
export async function disconnectWhatsAppNumber(id: number): Promise<TChannelConnection> {
  await axios.delete<TWhatsAppNumber>(`/integrations/whatsapp/numbers/${id}`);
  return refreshedWhatsApp();
}

/** "Disconnect WhatsApp" on the whole card: every number, one by one. */
async function disconnectAllWhatsApp(): Promise<TChannelConnection> {
  const { data } = await axios.get<TWhatsAppNumber[]>("/integrations/whatsapp/numbers");
  for (const n of data) await axios.delete(`/integrations/whatsapp/numbers/${n.id}`);
  return refreshedWhatsApp();
}

/* ------------------------- Meta: Instagram + Messenger (real API) ------------------------- */

const META_CONNECTION_ID = "conn-meta";

/** A Page that stopped working (token revoked, Page access removed) and needs a reconnect. */
export const isMetaPageBroken = (p: TMetaPage) => p.status === "error" || p.needsReauth;

/** How a Page is named in messages: its Instagram handle, else the Page name. */
export const metaPageName = (p: TMetaPage) => (p.igUsername ? `@${p.igUsername}` : p.pageName || "Facebook Page");

/**
 * The Meta card is one provider connection holding the company's Pages:
 * disconnected with none, "action needed" when any Page is broken, otherwise connected.
 */
function toMetaConnection(pages: TMetaPage[]): TChannelConnection {
  const broken = pages.find(isMetaPageBroken);
  return {
    id: META_CONNECTION_ID,
    provider: "meta",
    status: pages.length === 0 ? "disconnected" : broken ? "error" : "connected",
    accounts: [],
    enabledChannels: [
      ...(pages.some((p) => p.instagramEnabled) ? (["instagram"] as const) : []),
      ...(pages.some((p) => p.messengerEnabled) ? (["messenger"] as const) : []),
    ],
    error: broken
      ? `${metaPageName(broken)}: ${broken.lastError ?? "reconnect with Meta to keep receiving messages."}`
      : undefined,
    connectedAt: pages[0]?.connectedAt,
    pages,
  };
}

async function getMetaConnection(): Promise<TChannelConnection> {
  try {
    return await refreshedMeta();
  } catch (e) {
    // One provider failing to load must not hide the others on the page.
    return {
      id: META_CONNECTION_ID,
      provider: "meta",
      status: "error",
      accounts: [],
      enabledChannels: [],
      error: getErrorFromBackend(e),
      loadFailed: true,
    };
  }
}

export async function getMetaInboxConfig(): Promise<TMetaInboxConfig> {
  const { data } = await axios.get<TMetaInboxConfig>("/integrations/meta-inbox/config");
  return data;
}

async function refreshedMeta(): Promise<TChannelConnection> {
  const { data } = await axios.get<TMetaPage[]>("/integrations/meta-inbox/pages");
  return toMetaConnection(data);
}

/** Finishes "Connect with Meta": the backend connects every Page granted in the login (Messenger, and Instagram if linked). */
export async function connectMeta(code: string, redirectUri?: string): Promise<TChannelConnection> {
  const { data } = await axios.post<TMetaPage[]>("/integrations/meta-inbox/connect", { code, redirectUri });
  return toMetaConnection(data);
}

/** The Page channels that can be paused on their own. */
export type TMetaPageChannel = "instagram" | "messenger";

/** Pauses or resumes Instagram DMs or Messenger for one Page without disconnecting it. */
export async function setMetaChannelEnabled(
  id: number,
  channel: TMetaPageChannel,
  enabled: boolean
): Promise<TChannelConnection> {
  const body = channel === "instagram" ? { instagramEnabled: enabled } : { messengerEnabled: enabled };
  await axios.patch<TMetaPage>(`/integrations/meta-inbox/pages/${id}`, body);
  return refreshedMeta();
}

/** Disconnects one Page; the company's other Pages keep working. */
export async function disconnectMetaPage(id: number): Promise<TChannelConnection> {
  await axios.delete<TMetaPage>(`/integrations/meta-inbox/pages/${id}`);
  return refreshedMeta();
}

/* ---------------------------------- Telegram (mock) ---------------------------------- */

export async function connectTelegram({ token }: TConnectTelegramPayload): Promise<TChannelConnection> {
  await delay(900);
  if (!TELEGRAM_TOKEN_RE.test(token.trim())) {
    throw apiError("That doesn't look like a valid bot token. Copy it again from @BotFather.");
  }
  return saveTelegram({
    ...telegramConnection,
    status: "connected",
    accounts: [{ channel: "telegram", label: "Bot", value: "@NexgenTSalesBot" }],
    enabledChannels: ["telegram"],
    error: undefined,
    connectedAt: new Date().toISOString(),
  });
}

/* --------------------------------------------------------------------------------------- */

export async function getChannelConnections(): Promise<TChannelConnection[]> {
  const [meta, whatsapp] = await Promise.all([getMetaConnection(), getWhatsAppConnection()]);
  // Card order: Meta, WhatsApp, Telegram.
  return [meta, whatsapp, telegramConnection];
}

export async function disconnectChannel(provider: TConnectableProvider): Promise<TChannelConnection> {
  if (provider === "whatsapp") return disconnectAllWhatsApp();
  if (provider === "meta") throw apiError("Disconnect each Page from its own row");
  await delay();
  return saveTelegram({
    ...telegramConnection,
    status: "disconnected",
    accounts: [],
    enabledChannels: [],
    error: undefined,
    connectedAt: undefined,
  });
}
